if (!customElements.get('cart-undo-toast')) {
  class CartUndoToast extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.pauseTimer = this.pauseTimer.bind(this);
      this.resumeTimer = this.resumeTimer.bind(this);
    }

    connectedCallback() {
      // The pill is what shows/hides; the host (and its live region) stays in
      // the accessibility tree so announcements are never made inside a hidden subtree.
      this.pillElement = this.querySelector('.wonderly-cart-undo__pill');
      this.labelElement = this.querySelector('.wonderly-cart-undo__label');
      this.undoButton = this.querySelector('.wonderly-cart-undo__undo');
      this.closeButton = this.querySelector('.wonderly-cart-undo__close');
      this.liveRegion = this.querySelector('[data-undo-live]');
      this.defaultLabel = this.labelElement.textContent;
      this.duration = parseInt(this.dataset.duration, 10) || 8000;
      this.pending = null;
      this.current = null;
      this.busy = false;
      this.timer = null;
      this.viaKeyboard = false;

      if (!this.initialized) {
        this.initialized = true;
        this.undoButton.addEventListener('click', () => this.undo());
        this.closeButton.addEventListener('click', () => this.hide());
        this.addEventListener('mouseenter', this.pauseTimer);
        this.addEventListener('mouseleave', this.resumeTimer);
        this.addEventListener('focusin', this.pauseTimer);
        this.addEventListener('focusout', this.resumeTimer);
      }

      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('change', this.onDocumentChange, true);
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('change', this.onDocumentChange, true);
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      this.clearTimer();
    }

    readSnapshot() {
      const dataElement = document.querySelector('[data-wonderly-cart-undo-data]');
      if (!dataElement) return [];
      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        return [];
      }
    }

    stashLine(index) {
      if (!index) return;
      const entry = this.readSnapshot()[index - 1];
      // Flexi-Box parent/child lines are excluded from undo: a plain re-add
      // cannot restore the nested parent/child relationship.
      if (!entry || entry.virtual || entry.flexi || !entry.raw) return;
      const raw = entry.raw;
      const quantityInput = document.getElementById(`Quantity-${index}`);
      if (quantityInput && quantityInput.dataset.quantityVariantId !== String(raw.id)) return;
      const sellingPlan =
        raw.selling_plan_allocation &&
        raw.selling_plan_allocation.selling_plan &&
        raw.selling_plan_allocation.selling_plan.id;
      this.pending = {
        key: raw.key,
        id: raw.id,
        quantity: raw.quantity,
        // raw comes from Liquid `item | json` — cart AJAX shape, where
        // properties is the object /cart/add.js expects (item.properties in
        // Liquid is an array of pairs and gets rejected with a 400).
        properties: raw.properties || {},
        selling_plan: sellingPlan || null,
        title: raw.product_title || '',
        stashedAt: Date.now(),
      };
    }

    onDocumentClick(event) {
      if (!(event.target instanceof Element)) return;
      const removeButton = event.target.closest('cart-remove-button');
      if (removeButton && removeButton.closest('cart-items, cart-drawer-items')) {
        // detail === 0: activated with Enter/Space — the toast then takes focus
        // so a keyboard user can reach "Undo" before the timer runs out.
        this.viaKeyboard = event.detail === 0;
        this.stashLine(parseInt(removeButton.dataset.index, 10));
        return;
      }
      // A checkout click can trigger the Grocerist fee-observer cart rebuild
      // (/cart/clear.js then re-add) — an undo landing mid-rebuild would corrupt it.
      if (event.target.closest('[data-cart-checkout], #checkout')) {
        this.pending = null;
        this.hide();
      }
    }

    onDocumentChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.classList.contains('quantity__input') || !input.closest('cart-items, cart-drawer-items')) return;
      if (parseInt(input.value, 10) === 0) {
        this.viaKeyboard = true;
        this.stashLine(parseInt(input.dataset.index, 10));
      }
    }

    onCartUpdate(event) {
      if (event.source !== 'cart-items') return;
      const pending = this.pending;
      this.pending = null;
      if (!pending || Date.now() - pending.stashedAt > 15000) return;
      const items = (event.cartData && event.cartData.items) || [];
      if (items.some((item) => item.key === pending.key)) return;
      this.show(pending);
    }

    show(line) {
      this.current = line;
      this.classList.remove('wonderly-cart-undo--error');
      this.labelElement.textContent = this.defaultLabel;
      this.undoButton.disabled = false;
      this.pillElement.hidden = false;
      this.announce(`${this.defaultLabel}. ${this.undoButton.textContent.trim()}.`);
      if (this.viaKeyboard) {
        this.viaKeyboard = false;
        requestAnimationFrame(() => this.undoButton.focus());
      }
      this.startTimer();
    }

    hide() {
      const hadFocus = this.hadFocus || this.contains(document.activeElement);
      this.hadFocus = false;
      if (hadFocus) {
        // Keep focus inside the drawer when the toast goes away (so Escape and
        // the focus trap keep working); on the cart page just drop it.
        const drawerDialog = this.closest('cart-drawer') && this.closest('cart-drawer').querySelector('.drawer__inner');
        if (drawerDialog) drawerDialog.focus();
        else document.activeElement.blur();
      }
      this.pillElement.hidden = true;
      this.current = null;
      this.clearTimer();
    }

    startTimer() {
      this.clearTimer();
      this.timer = setTimeout(() => this.hide(), this.duration);
    }

    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    pauseTimer() {
      this.clearTimer();
    }

    resumeTimer(event) {
      if (event && event.type === 'focusout' && this.contains(event.relatedTarget)) return;
      if (!this.pillElement.hidden && !this.busy) this.startTimer();
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      requestAnimationFrame(() => {
        this.liveRegion.textContent = message;
      });
    }

    showError(message) {
      const errorMessage =
        message || this.dataset.errorText || (window.cartStrings && window.cartStrings.error) || '';
      this.classList.add('wonderly-cart-undo--error');
      this.labelElement.textContent = errorMessage;
      this.current = null;
      this.pillElement.hidden = false;
      const cartErrors = document.getElementById('cart-errors');
      if (cartErrors) cartErrors.textContent = errorMessage;
      this.announce(errorMessage);
      this.startTimer();
    }

    undo() {
      if (this.busy || !this.current) return;
      // One cart surface per page: the cart page's <cart-items> or the cart
      // drawer's <cart-drawer-items> (same getSectionsToRender contract).
      const mainCartItems = document.querySelector('#main-cart-items, #CartDrawer-CartItems');
      if (mainCartItems && mainCartItems.classList.contains('cart__items--disabled')) return;
      const cartItemsElement = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
      if (!cartItemsElement) return;

      const line = this.current;
      this.busy = true;
      // Disabling the button drops focus to <body> — remember we had it so
      // hide() can hand focus back to the drawer.
      this.hadFocus = this.contains(document.activeElement);
      this.undoButton.disabled = true;
      this.clearTimer();

      const item = {
        id: line.id,
        quantity: line.quantity,
        properties: line.properties || {},
      };
      if (line.selling_plan) item.selling_plan = line.selling_plan;

      const body = JSON.stringify({
        items: [item],
        sections: [...new Set(cartItemsElement.getSectionsToRender().map((section) => section.section))],
        sections_url: window.location.pathname,
      });

      fetch(routes.cart_add_url, { ...fetchConfig(), body })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description);
            return;
          }
          // Mirror CartItems.updateQuantity's render path (assets/cart.js).
          // add.js succeeded, so the cart cannot be empty.
          cartItemsElement.classList.remove('is-empty');
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) cartFooter.classList.remove('is-empty');
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) cartDrawer.classList.remove('is-empty');
          cartItemsElement.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement || !response.sections || !response.sections[section.section]) return;
            const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = cartItemsElement.getSectionInnerHTML(
              response.sections[section.section],
              section.selector
            );
          });
          this.announce((this.dataset.restoredText || '').replace('[title]', line.title || ''));
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wonderly-cart-undo', cartData: response });
          this.hide();
        })
        .catch(() => {
          this.showError();
        })
        .finally(() => {
          this.busy = false;
          this.undoButton.disabled = false;
        });
    }
  }

  customElements.define('cart-undo-toast', CartUndoToast);
}
